import type { ILocalizationContainer, ILocalizationTypes } from "@edaz/services/baseLocalizationService";
import BaseLocalizationService from "@edaz/services/baseLocalizationService";
import { bound } from "@frui.ts/helpers";
import { action, observable } from "mobx";
import type { EntityNames, InternalEnumsMap } from "config";
import * as config from "config";
import type { BankAccountFormatType } from "entities/bankAccountFormatType";
import type ChargeValidity from "entities/chargeValidity";
import type CreditExposureType from "entities/creditExposureType";
import type DistributorType from "entities/distributorType";
import type DistributorStatus from "entities/distributorStatus";
import type DistributorPaymentRequestType from "entities/distributorPaymentRequestType";
import type FuelType from "entities/fuelType";
import type GatewayState from "entities/gatewayState";
import type KioskStatus from "entities/kioskStatus";
import type ChargeOrderStatus from "entities/chargeOrderStatus";
import type SystemComponentOutageState from "entities/systemComponentOutageState";
import type ChargeChangeInitiator from "entities/chargeChangeInitiator";
import type ChargeChangeOrigin from "entities/chargeChangeOrigin";
import type PaymentType from "entities/paymentType";
import type PermissionType from "entities/permissionType";
import type SourceType from "entities/sourceType";
import type ChargeRefundationStatus from "entities/chargeRefundationStatus";
import type RevenueSourceType from "entities/revenueSourceType";
import type RoadType from "entities/roadType";
import type TransferType from "entities/transferType";
import type UnpairedPaymentStatus from "entities/unpairedPaymentStatus";
import type { UserType } from "entities/userType";
import type LocalizationRepository from "repositories/localizationRepository";
import { czechTimeZone } from "utils/dateUtils";
import type EventBus from "./eventBus";
import { localeChanged } from "./eventBus";
import type LocaleProvider from "./localeProvider";
import type RefundType from "entities/refundType";
import type RefundDisplayStatus from "entities/refundDisplayStatus";
import type DistributorsPaymentRequestStatus from "entities/distributorPaymentRequestStatus";
import type SystemComponentState from "entities/systemComponentState";
import type BankCodeType from "entities/bankCodeType";
import type CreditExposureNotificationStatus from "entities/creditExposureNotificationStatus";
import type CreditExposureState from "entities/creditExposureState";
import type OutgoingPayGatePaymentStatus from "entities/outgoingPayGatePaymentStatus";
import type OutgoingTransferPaymentStatus from "entities/outgoingTransferPaymentStatus";
import { formatTextWithUnbreakableRules, unbreakableSpaces } from "../utils/localizationUtils";
import type ReportRequestStatus from "entities/reportRequestStatus";
import type ChargeDisplayStatus from "../entities/chargeDisplayStatus";
import type RefundPaymentType from "entities/refundPaymentType";
import type RejectedCustomerRefundCandidateStatus from "../entities/rejectedCustomerRefundCandidateStatus";
import type RefundDisplayType from "../entities/refundDisplayType";
import type CustomerRefundPaymentType from "../entities/customerRefundPaymentType";
import type KafkaDltTopic from "../entities/kafkaDltTopic";
import type KafkaDltMessageType from "../entities/kafkaDltMessageType";
import type DeadLetterTopicActionType from "../entities/deadLetterTopicActionType";
import type DistributorRefundCandidateApprovalRejectionReason from "../entities/distributorRefundCandidateApprovalRejectionReason";
import type ExemptionRegistrationMethod from "entities/exemptionRegistrationMethod";
import type ChargeOrderPaymentDisplayType from "entities/chargeOrderPaymentDisplayType";
import type VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import type ChargePropertyChangeDisplayType from "entities/chargePropertyChangeDisplayType";
import type ChangedChargeProperty from "entities/changedChargeProperty";
import type ExemptionPropertyChangeDisplayType from "entities/exemptionPropertyChangeDisplayType";
import type ExemptionChangeOrigin from "entities/exemptionChangeOrigin";
import type BankAccountStatus from "entities/bankAccountStatus";
import type VehicleRegisterVehicleChangeOrigin from "entities/vehicleRegisterVehicleChangeOrigin";
import type VehicleRegisterVehiclePropertyChangeDisplayType from "entities/vehicleRegisterVehiclePropertyChangeDisplayType";

export interface LocalizationContainer extends ILocalizationContainer {
  attributes: {
    [key in EntityNames]: ILocalizationContainer;
  };
  models: ILocalizationContainer;
  general: ILocalizationContainer;
  modules: {
    [key in config.Modules]: ILocalizationContainer;
  };
  errors: {
    [key in config.KnownErrorCodes]: ILocalizationTypes;
  };
}

export type InternalEnums = (typeof InternalEnumsMap)[keyof typeof InternalEnumsMap];

export type InternalEnumNames = keyof typeof InternalEnumsMap;

export default class LocalizationService extends BaseLocalizationService<LocalizationContainer> {
  @observable locs: LocalizationContainer;

  constructor(
    private localeProvider: LocaleProvider,
    protected localizationProvider: LocalizationRepository,
    private eventBus: EventBus
  ) {
    super(localizationProvider);
    this.eventBus.subscribe(localeChanged, this.changeLocale);
  }

  get locale(): string {
    return this.localeProvider.locale;
  }

  @bound
  translate(code: string, size?: number): string {
    return formatTextWithUnbreakableRules(super.translate(code, size));
  }

  @bound
  translateGeneral(code: string, size?: number) {
    return this.translate(`general.${code}`, size);
  }

  @bound
  translateModule(module: config.Modules, code: string, size?: number) {
    return this.translate(`modules.${module}.${code}`, size);
  }

  translateEnum(enumName: "bankAccountFormatType", value: BankAccountFormatType, size?: number): string;
  translateEnum(enumName: "bankCodeType", value: BankCodeType, size?: number): string;
  translateEnum(enumName: "bankAccountStatus", value: BankAccountStatus, size?: number): string;
  translateEnum(enumName: "customerRefundPaymentType", value: CustomerRefundPaymentType, size?: number): string;
  translateEnum(enumName: "creditExposureNotificationStatus", value: CreditExposureNotificationStatus, size?: number): string;
  translateEnum(enumName: "creditExposureState", value: CreditExposureState, size?: number): string;
  translateEnum(enumName: "creditExposureType", value: CreditExposureType, size?: number): string;
  translateEnum(enumName: "deadLetterTopicActionType", value: DeadLetterTopicActionType, size?: number): string;
  translateEnum(enumName: "distributorType", value: DistributorType, size?: number): string;
  translateEnum(enumName: "distributorStatus", value: DistributorStatus, size?: number): string;
  translateEnum(enumName: "distributorPaymentRequestStatus", value: DistributorsPaymentRequestStatus, size?: number): string;
  translateEnum(
    enumName: "distributorRefundCandidateApprovalRejectionReason",
    value: DistributorRefundCandidateApprovalRejectionReason,
    size?: number
  ): string;
  translateEnum(enumName: "distributorPaymentRequestType", value: DistributorPaymentRequestType, size?: number): string;
  translateEnum(enumName: "fuelType", value: FuelType, size?: number): string;
  translateEnum(enumName: "gatewayState", value: GatewayState, size?: number): string;
  translateEnum(enumName: "changedChargeProperty", value: ChangedChargeProperty, size?: number): string;
  translateEnum(enumName: "chargePropertyChangeDisplayType", value: ChargePropertyChangeDisplayType, size?: number): string;
  translateEnum(enumName: "exemptionPropertyChangeDisplayType", value: ExemptionPropertyChangeDisplayType, size?: number): string;
  translateEnum(enumName: "exemptionChangeOrigin", value: ExemptionChangeOrigin, size?: number): string;
  translateEnum(enumName: "chargeDisplayStatus", value: ChargeDisplayStatus, size?: number): string;
  translateEnum(enumName: "chargeValidity", value: ChargeValidity, size?: number): string;
  translateEnum(enumName: "chargeRefundationStatus", value: ChargeRefundationStatus, size?: number): string;
  translateEnum(enumName: "chargeChangeInitiator", value: ChargeChangeInitiator, size?: number): string;
  translateEnum(enumName: "chargeChangeOrigin", value: ChargeChangeOrigin, size?: number): string;
  translateEnum(enumName: "chargeOrderStatus", value: ChargeOrderStatus, size?: number): string;
  translateEnum(enumName: "kioskStatus", value: KioskStatus, size?: number): string;
  translateEnum(enumName: "outgoingPayGatePaymentStatus", value: OutgoingPayGatePaymentStatus, size?: number): string;
  translateEnum(enumName: "outgoingTransferPaymentStatus", value: OutgoingTransferPaymentStatus, size?: number): string;
  translateEnum(enumName: "paymentType", value: PaymentType, size?: number): string;
  translateEnum(enumName: "paymentDisplayType", value: ChargeOrderPaymentDisplayType, size?: number): string;
  translateEnum(
    enumName: "vehicleRegisterSynchronizationStatus",
    value: VehicleRegisterSynchronizationStatus,
    size?: number
  ): string;
  translateEnum(enumName: "vehicleRegisterVehicleFuelType", value: VehicleRegisterVehicleFuelType, size?: number): string;
  translateEnum(
    enumName: "vehicleRegisterVehicleClassifiedFuelType",
    value: VehicleRegisterVehicleClassifiedFuelType,
    size?: number
  ): string;
  translateEnum(enumName: "permissionType", value: PermissionType, size?: number): string;
  translateEnum(enumName: "refundDisplayType", value: RefundDisplayType, size?: number): string;
  translateEnum(enumName: "refundDisplayStatus", value: RefundDisplayStatus, size?: number): string;
  translateEnum(enumName: "refundType", value: RefundType, size?: number): string;
  translateEnum(enumName: "refundPaymentType", value: RefundPaymentType, size?: number): string;
  translateEnum(enumName: "reportRequestStatus", value: ReportRequestStatus, size?: number): string;
  translateEnum(enumName: "revenueSourceType", value: RevenueSourceType, size?: number): string;
  translateEnum(
    enumName: "rejectedCustomerRefundCandidateStatus",
    value: RejectedCustomerRefundCandidateStatus,
    size?: number
  ): string;
  translateEnum(enumName: "roadType", value: RoadType, size?: number): string;
  translateEnum(enumName: "sourceType", value: SourceType, size?: number): string;
  translateEnum(enumName: "systemComponentState", value: SystemComponentState, size?: number): string;
  translateEnum(enumName: "systemComponentOutageState", value: SystemComponentOutageState, size?: number): string;
  translateEnum(enumName: "kafkaDltMessageType", value: KafkaDltMessageType, size?: number): string;
  translateEnum(enumName: "kafkaDltTopic", value: KafkaDltTopic, size?: number): string;
  translateEnum(enumName: "transferType", value: TransferType, size?: number): string;
  translateEnum(enumName: "unpairedPaymentStatus", value: UnpairedPaymentStatus, size?: number): string;
  translateEnum(enumName: "userType", value: UserType, size?: number): string;
  translateEnum(enumName: "vehicleRegisterVehicleStatus", value: VehicleRegisterVehicleStatus, size?: number): string;
  translateEnum(
    enumName: "vehicleRegisterVehiclePropertyChangeDisplayType",
    value: VehicleRegisterVehiclePropertyChangeDisplayType,
    size?: number
  ): string;
  translateEnum(enumName: "vehicleRegisterVehicleChangeOrigin", value: VehicleRegisterVehicleChangeOrigin, size?: number): string;
  translateEnum(enumName: "exemptionRegistrationMethod", value: ExemptionRegistrationMethod, size?: number): string;
  translateEnum(enumName: InternalEnumNames, value: any, size?: number) {
    return this.translate(`enumeration.${enumName}.${value}`, size);
  }

  @bound
  translateModel(model: EntityNames | string, size?: number) {
    return this.translate(`models.${model}`, size);
  }

  @bound
  translateError(errorCode: config.KnownErrorCodes) {
    return this.translate(`errors.${errorCode}`);
  }

  @bound
  translateAttribute(model: EntityNames | string, attribute: string, size?: number): string {
    return this.translate(`attributes.${model}.${attribute}`, size);
  }

  formatDate(date?: Date | null, options?: Intl.DateTimeFormatOptions): string {
    return super.formatDate(date ?? undefined, { ...options, timeZone: czechTimeZone });
  }

  formatDateTime(date?: Date | null, options?: Intl.DateTimeFormatOptions) {
    return super.formatDateTime(date ?? undefined, { ...options, timeZone: czechTimeZone });
  }

  formatTime(date?: Date | null, options?: Intl.DateTimeFormatOptions) {
    return unbreakableSpaces(super.formatTime(date ?? undefined, { ...options, timeZone: czechTimeZone }));
  }

  @action.bound
  changeLocale() {
    this.initialize();
  }
}
